import i18n from '../../../i18n/config';
import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/schematics/VehicleTelemetryModal/ChartsTab.jsx
// ✅ ENHANCED VERSION - System events + Day separators + Map click sync
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Button,
    IconButton,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Chip,
    TextField,
    Tooltip,
    useTheme,
    useMediaQuery,
    Skeleton,
    FormControlLabel,
    Switch,
    Divider
} from '@mui/material';
import {
    Speed as PressureIcon,
    Thermostat as TemperatureIcon,
    Vibration as AccelerationIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Fullscreen as FullscreenIcon,
    Refresh as RefreshIcon,
    Timeline as TimelineIcon,
    KeyboardArrowLeft as KeyboardArrowLeftIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Menu as MenuIcon,
    PowerSettingsNew as PowerIcon,
    PowerOff as PowerOffIcon,
    CloudDone as OnlineIcon,
    CloudOff as OfflineIcon
} from '@mui/icons-material';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
    TIME_RANGE_OPTIONS,
    SERIES_COLORS,
    METRIC_TYPE_MAP,
    HIGHCHARTS_BOOST_CONFIG
} from './constants';
import {
    getMetricUnit,
    getMetricDisplayName,
    formatValue,
    getXAxisFormat,
    createSchematicLayout,
    getAvailablePositions,
    convertTelemetryValue,
    unitConversionUtils as createUnitConversionUtils
} from './utils';
import { api } from '../../../services/api';

const ChartsTab = ({
    vehicle,
    telemetryData,
    telemetryMeta,
    telemetryLoading,
    selectedPositions,
    setSelectedPositions,
    customDateRange,
    setCustomDateRange,
    selectedTimeRange,
    setSelectedTimeRange,
    refetchTelemetry,
    alarmProfiles,
    unitPreferences,
    formatDateTimeLocal,
    formatDateLocal,
    formatTimeLocal,
    formatDayMonthLocal,
    effectiveTimezone,
    calculateDateRange,
    setHoveredTimestamp,
    mapClickedTimestamp = null // 🆕 Timestamp del punto clickeado en el mapa
}) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Estados para gráficas
    const [activeCharts, setActiveCharts] = useState([
        { id: 1, metric: 'pressure', zoomDomain: null }
    ]);
    const [nextChartId, setNextChartId] = useState(2);
    const [fullscreenChart, setFullscreenChart] = useState(null);
    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
    const [isLoadingRange, setIsLoadingRange] = useState(false);
    const [processedDataCache, setProcessedDataCache] = useState({});

    // Estados para eventos del sistema
    const [systemEvents, setSystemEvents] = useState([]);
    const [systemEventsLoading, setSystemEventsLoading] = useState(false);
    const [visibleEventTypes, setVisibleEventTypes] = useState({
        'HB ONLINE': false,
        'HB OFFLINE': false,
        'POWEROFF': false,
        'STARTUP': false
    });

    const chartRefs = useRef({});
    const isAutoSwitchingRef = useRef(false);

    // Unit conversion utilities
    const unitConversionUtils = useMemo(
        () => createUnitConversionUtils(unitPreferences),
        [unitPreferences]
    );

    const getTimeZoneOffsetMs = useCallback((timestamp, timeZone) => {
        if (!timeZone || !Intl?.DateTimeFormat) return 0;

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });

        const parts = formatter.formatToParts(new Date(timestamp));
        const values = {};

        parts.forEach(part => {
            if (part.type !== 'literal') {
                values[part.type] = Number(part.value);
            }
        });

        const asUTC = Date.UTC(
            values.year,
            (values.month || 1) - 1,
            values.day || 1,
            values.hour || 0,
            values.minute || 0,
            values.second || 0
        );

        return asUTC - timestamp;
    }, []);

    const zonedMidnightToUtc = useCallback((year, month, day, timeZone) => {
        if (!timeZone) {
            return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
        }

        const baseUtc = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
        let utcTimestamp = baseUtc - getTimeZoneOffsetMs(baseUtc, timeZone);
        utcTimestamp = baseUtc - getTimeZoneOffsetMs(utcTimestamp, timeZone);

        return utcTimestamp;
    }, [getTimeZoneOffsetMs]);

    const formatSeparatorLabel = useCallback((timestamp) => {
        try {
            return new Intl.DateTimeFormat('es-MX', {
                day: '2-digit',
                month: 'short',
                timeZone: effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
            }).format(new Date(timestamp));
        } catch (error) {
            return formatDayMonthLocal ? formatDayMonthLocal(timestamp) : formatDateLocal(timestamp);
        }
    }, [effectiveTimezone, formatDayMonthLocal, formatDateLocal]);

    // 🆕 Efecto para sincronizar click del mapa con tooltips de gráficas
    useEffect(() => {
        if (!mapClickedTimestamp || !chartRefs.current) return;

        const timestamp = new Date(mapClickedTimestamp).getTime();

        // Mostrar tooltip en todas las gráficas activas
        Object.keys(chartRefs.current).forEach(chartId => {
            const chartRef = chartRefs.current[chartId];
            if (!chartRef || !chartRef.chart) return;

            const chart = chartRef.chart;

            // Buscar el punto más cercano al timestamp en todas las series
            let closestPoint = null;
            let minDiff = Infinity;

            chart.series.forEach(series => {
                if (series.visible && series.points) {
                    series.points.forEach(point => {
                        const diff = Math.abs(point.x - timestamp);
                        if (diff < minDiff) {
                            minDiff = diff;
                            closestPoint = point;
                        }
                    });
                }
            });

            // Si encontramos un punto cercano (dentro de 15 minutos), mostrar tooltip
            if (closestPoint && minDiff < 15 * 60 * 1000) {
                // Forzar el tooltip a mostrarse
                chart.tooltip.refresh(
                    chart.series
                        .filter(s => s.visible)
                        .map(s => {
                            // Buscar punto en esta serie con el mismo x
                            return s.points.find(p => Math.abs(p.x - closestPoint.x) < 1000);
                        })
                        .filter(p => p)
                );

                // Opcional: centrar el crosshair
                chart.xAxis[0].drawCrosshair(null, closestPoint);
            }
        });
    }, [mapClickedTimestamp]);

    // Cargar eventos del sistema cuando cambie el rango de fechas
    useEffect(() => {
        const fetchSystemEvents = async () => {
            if (!vehicle?.id || !customDateRange.startDate || !customDateRange.endDate) {
                return;
            }

            try {
                setSystemEventsLoading(true);
                const response = await api.telemetry.getVehicleSystemEvents(vehicle.id, {
                    start_date: customDateRange.startDate,
                    end_date: customDateRange.endDate
                });

                if (response.data?.events) {
                    console.log(t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.system_events_loaded'), response.data.event_counts);
                    setSystemEvents(response.data.events);
                }
            } catch (error) {
                console.error('Error loading system events:', error);
            } finally {
                setSystemEventsLoading(false);
            }
        };

        fetchSystemEvents();
    }, [vehicle?.id, customDateRange.startDate, customDateRange.endDate]);

    // Limpiar caché cuando cambian los datos
    useEffect(() => {
        setProcessedDataCache({});
    }, [telemetryData]);

    // Calcular líneas de separación de días para vista 1D
    const calculateDaySeparators = useCallback(() => {
        if (selectedTimeRange !== '1day' || !customDateRange.startDate || !customDateRange.endDate) {
            return [];
        }

        const plotLines = [];
        const [startYear, startMonth, startDay] = customDateRange.startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = customDateRange.endDate.split('-').map(Number);

        if ([startYear, startMonth, startDay, endYear, endMonth, endDay].some(Number.isNaN)) {
            return [];
        }

        const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));

        // Iterar por cada día en el rango
        let currentDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);

        while (currentDate <= endDate) {
            const year = currentDate.getUTCFullYear();
            const month = currentDate.getUTCMonth() + 1;
            const day = currentDate.getUTCDate();
            const timestamp = zonedMidnightToUtc(year, month, day, effectiveTimezone);

            plotLines.push({
                color: theme.palette.divider,
                width: 2,
                value: timestamp,
                dashStyle: 'Solid',
                label: {
                    text: formatSeparatorLabel(timestamp),
                    align: 'center',
                    rotation: 0,
                    y: 15,
                    style: {
                        color: theme.palette.text.secondary,
                        fontWeight: 'bold',
                        fontSize: '11px',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.divider}`
                    }
                },
                zIndex: 4
            });

            // Avanzar al siguiente día
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }

        console.log(t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.day_separators_created'), plotLines.length);
        return plotLines;
    }, [selectedTimeRange, customDateRange.startDate, customDateRange.endDate, effectiveTimezone, formatSeparatorLabel, theme, zonedMidnightToUtc]);

    // Procesar eventos del sistema para Highcharts
    const processSystemEvents = useCallback(() => {
        if (!systemEvents || systemEvents.length === 0) {
            return { plotBands: [], plotLines: [] };
        }

        const plotBands = [];
        const plotLines = [];

        // Ordenar eventos por timestamp
        const sortedEvents = [...systemEvents].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        // Crear bandas para HB ONLINE/OFFLINE (períodos)
        let currentState = null;
        let stateStartTime = null;

        sortedEvents.forEach((event, index) => {
            const timestamp = new Date(event.timestamp).getTime();
            const eventType = event.type;

            if (eventType === 'HB ONLINE' || eventType === 'HB OFFLINE') {
                // Si hay un cambio de estado, cerrar la banda anterior
                if (currentState && currentState !== eventType && stateStartTime) {
                    const isVisible = visibleEventTypes[currentState];

                    if (isVisible) {
                        plotBands.push({
                            from: stateStartTime,
                            to: timestamp,
                            color: currentState === 'HB ONLINE'
                                ? 'rgba(76, 175, 80, 0.08)'
                                : 'rgba(158, 158, 158, 0.08)',
                            label: {
                                text: currentState === 'HB ONLINE' ? '🟢 Online' : '⚫ Offline',
                                style: {
                                    color: currentState === 'HB ONLINE' ? '#4CAF50' : '#9E9E9E',
                                    fontSize: '9px',
                                    fontWeight: 'bold'
                                },
                                align: 'center',
                                verticalAlign: 'top',
                                y: 5
                            },
                            zIndex: 0
                        });
                    }
                }

                // Iniciar nueva banda
                currentState = eventType;
                stateStartTime = timestamp;

                // Si es el último evento, cerrar la banda hasta el presente
                if (index === sortedEvents.length - 1) {
                    const isVisible = visibleEventTypes[currentState];

                    if (isVisible) {
                        plotBands.push({
                            from: stateStartTime,
                            to: new Date().getTime(),
                            color: currentState === 'HB ONLINE'
                                ? 'rgba(76, 175, 80, 0.08)'
                                : 'rgba(158, 158, 158, 0.08)',
                            label: {
                                text: currentState === 'HB ONLINE' ? '🟢 Online' : '⚫ Offline',
                                style: {
                                    color: currentState === 'HB ONLINE' ? '#4CAF50' : '#9E9E9E',
                                    fontSize: '9px',
                                    fontWeight: 'bold'
                                },
                                align: 'center',
                                verticalAlign: 'top',
                                y: 5
                            },
                            zIndex: 0
                        });
                    }
                }
            }

            // POWEROFF - Línea vertical roja
            if (eventType === 'POWEROFF' && visibleEventTypes.POWEROFF) {
                plotLines.push({
                    color: '#F44336',
                    width: 2,
                    value: timestamp,
                    dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                    label: {
                        text: '🔴 OFF',
                        align: 'center',
                        rotation: 0,
                        y: -5,
                        style: {
                            color: '#F44336',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '2px 4px',
                            borderRadius: '3px'
                        }
                    },
                    zIndex: 3
                });
            }

            // STARTUP - Línea vertical verde
            if (eventType === 'STARTUP' && visibleEventTypes.STARTUP) {
                plotLines.push({
                    color: '#4CAF50',
                    width: 2,
                    value: timestamp,
                    dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                    label: {
                        text: '🟢 ON',
                        align: 'center',
                        rotation: 0,
                        y: -5,
                        style: {
                            color: '#4CAF50',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '2px 4px',
                            borderRadius: '3px'
                        }
                    },
                    zIndex: 3
                });
            }
        });

        console.log('📈 Generated plot bands:', plotBands.length, 'plot lines:', plotLines.length);
        return { plotBands, plotLines };
    }, [systemEvents, visibleEventTypes]);

    // Process chart data con caché
    const processChartData = useCallback((selectedMetric, chart) => {
        if (!telemetryData || !Array.isArray(telemetryData)) {
            return { series: [], categories: [] };
        }

        const cacheKey = `${selectedMetric}_${selectedPositions.sort().join(',')}_${telemetryData.length}_${selectedTimeRange}`;

        if (processedDataCache[cacheKey]) {
            console.log('📦 Using cached data for:', cacheKey);
            return processedDataCache[cacheKey];
        }

        console.log('🔄 Processing new data for:', cacheKey);

        let filteredData = telemetryData;

        // Filtrado de posiciones
        if (!selectedPositions.includes('all') && selectedPositions.length > 0) {
            filteredData = telemetryData.filter(item => {
                const actualSensorId = item.raw_payload?.id?.toString() || item.sensor_id;
                const sensorAssignment = vehicle.vehicle_sensors?.find(vs => vs.sensor_id.toString() === actualSensorId.toString());
                return sensorAssignment && selectedPositions.includes(sensorAssignment.position);
            });
        } else {
            filteredData = telemetryData.filter(item => {
                const actualSensorId = item.raw_payload?.id?.toString() || item.sensor_id;
                const sensorAssignment = vehicle.vehicle_sensors?.find(vs => vs.sensor_id.toString() === actualSensorId.toString());
                return !!sensorAssignment;
            });
        }

        if (selectedMetric === 'acceleration') {
            filteredData = filteredData.filter(item => item.type === 'ACC');
        } else {
            filteredData = filteredData.filter(item => item.type === 'PSI');
        }

        const dataByPosition = new Map();

        filteredData.forEach(item => {
            const actualSensorId = item.raw_payload?.id?.toString() || item.sensor_id;
            const sensorAssignment = vehicle.vehicle_sensors?.find(vs => vs.sensor_id.toString() === actualSensorId.toString());

            if (!sensorAssignment) return;

            const position = sensorAssignment.position;
            if (!dataByPosition.has(position)) {
                dataByPosition.set(position, []);
            }

            const rawTimestamp = item.time_stamp || item.created_at;
            let timestamp;

            if (typeof rawTimestamp === 'string') {
                if (!rawTimestamp.includes('Z') && !rawTimestamp.includes('+') && !rawTimestamp.includes('-', 10)) {
                    const utcTimestamp = rawTimestamp.includes('T') ? rawTimestamp + 'Z' : rawTimestamp + 'T00:00:00Z';
                    timestamp = new Date(utcTimestamp).getTime();
                } else {
                    timestamp = new Date(rawTimestamp).getTime();
                }
            } else {
                timestamp = new Date(rawTimestamp).getTime();
            }

            const convertedValue = convertTelemetryValue(item, selectedMetric, unitPreferences, unitConversionUtils);

            if (convertedValue !== null) {
                dataByPosition.get(position).push([timestamp, convertedValue]);
            }
        });

        const series = Array.from(dataByPosition.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([position, data]) => {
                data.sort((a, b) => a[0] - b[0]);

                const colorIndex = (position - 1) % SERIES_COLORS.length;

                return {
                    name: `${position}`,
                    data: data,
                    color: SERIES_COLORS[colorIndex],
                    turboThreshold: 50000,
                    boostThreshold: 1
                };
            });

        const result = { series, categories: [] };

        console.log('💾 Saving to cache:', cacheKey);
        setProcessedDataCache(prev => ({
            ...prev,
            [cacheKey]: result
        }));

        return result;
    }, [telemetryData, selectedPositions, vehicle?.vehicle_sensors, unitPreferences, unitConversionUtils, processedDataCache, selectedTimeRange]);

    // Get alarm profile plot lines
    const getAlarmProfilePlotLines = useCallback((chart) => {
        const plotLines = [];

        if (!alarmProfiles || alarmProfiles.length === 0) {
            return plotLines;
        }

        const currentMetric = METRIC_TYPE_MAP[chart.metric];
        if (!currentMetric) return plotLines;

        alarmProfiles.forEach((assignment, assignmentIndex) => {
            const profile = assignment.profile;
            if (!profile?.configurations) return;

            const relevantConfigs = profile.configurations.filter(config =>
                config.metric_type === currentMetric && config.enabled
            );

            relevantConfigs.forEach((config, configIndex) => {
                const colorOffset = (assignmentIndex * 30) + (configIndex * 10);

                if (config.threshold_min !== null && config.threshold_min !== undefined) {
                    let convertedMin = config.threshold_min;
                    if (chart.metric === 'pressure' && unitPreferences) {
                        convertedMin = unitConversionUtils.convertPressure(config.threshold_min, 'PSI');
                    } else if (chart.metric === 'temperature' && unitPreferences) {
                        convertedMin = unitConversionUtils.convertTemperature(config.threshold_min, 'C');
                    } else if (chart.metric === 'acceleration' && unitPreferences) {
                        convertedMin = unitConversionUtils.convertAcceleration(config.threshold_min, 'G');
                    }

                    plotLines.push({
                        color: `hsl(${(0 + colorOffset) % 360}, 70%, 45%)`,
                        width: 2,
                        value: parseFloat(convertedMin),
                        dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                        label: {
                            // text: `Min: ${convertedMin.toFixed(1)} ${getMetricUnit(chart.metric, unitPreferences)} (Eje ${config.axle_number})`,
                            text: `Min: ${convertedMin.toFixed(1)} ${getMetricUnit(chart.metric, unitPreferences)}`,

                            align: 'left',
                            x: 10,
                            style: {
                                color: `hsl(${(0 + colorOffset) % 360}, 70%, 35%)`,
                                fontWeight: 'bold',
                                fontSize: '10px'
                            }
                        },
                        zIndex: 5
                    });
                }

                if (config.threshold_max !== null && config.threshold_max !== undefined) {
                    let convertedMax = config.threshold_max;
                    if (chart.metric === 'pressure' && unitPreferences) {
                        convertedMax = unitConversionUtils.convertPressure(config.threshold_max, 'PSI');
                    } else if (chart.metric === 'temperature' && unitPreferences) {
                        convertedMax = unitConversionUtils.convertTemperature(config.threshold_max, 'C');
                    } else if (chart.metric === 'acceleration' && unitPreferences) {
                        convertedMax = unitConversionUtils.convertAcceleration(config.threshold_max, 'G');
                    }

                    plotLines.push({
                        color: `hsl(${(20 + colorOffset) % 360}, 70%, 50%)`,
                        width: 2,
                        value: parseFloat(convertedMax),
                        dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                        label: {
                            // text: `Max: ${convertedMax.toFixed(1)} ${getMetricUnit(chart.metric, unitPreferences)} (Eje ${config.axle_number})`,
                            text: `Max: ${convertedMax.toFixed(1)} ${getMetricUnit(chart.metric, unitPreferences)}`,

                            align: 'left',
                            x: 10,
                            style: {
                                color: `hsl(${(20 + colorOffset) % 360}, 70%, 40%)`,
                                fontWeight: 'bold',
                                fontSize: '10px'
                            }
                        },
                        zIndex: 5
                    });
                }
            });
        });

        return plotLines;
    }, [alarmProfiles, unitPreferences, unitConversionUtils]);

    // Get Y-axis range
    const getYAxisRange = useCallback((chart) => {
        const currentMetric = METRIC_TYPE_MAP[chart.metric];
        let maxThreshold = 0;
        let minThreshold = Number.MAX_VALUE;

        if (alarmProfiles && alarmProfiles.length > 0) {
            alarmProfiles.forEach(assignment => {
                const profile = assignment.profile;
                if (profile?.configurations) {
                    const relevantConfigs = profile.configurations.filter(config =>
                        config.metric_type === currentMetric && config.enabled
                    );

                    relevantConfigs.forEach(config => {
                        if (config.threshold_max !== null && config.threshold_max !== undefined) {
                            let convertedMax = config.threshold_max;
                            if (chart.metric === 'pressure' && unitPreferences) {
                                convertedMax = unitConversionUtils.convertPressure(config.threshold_max, 'PSI');
                            } else if (chart.metric === 'temperature' && unitPreferences) {
                                convertedMax = unitConversionUtils.convertTemperature(config.threshold_max, 'C');
                            } else if (chart.metric === 'acceleration' && unitPreferences) {
                                convertedMax = unitConversionUtils.convertAcceleration(config.threshold_max, 'G');
                            }
                            maxThreshold = Math.max(maxThreshold, parseFloat(convertedMax));
                        }
                        if (config.threshold_min !== null && config.threshold_min !== undefined) {
                            let convertedMin = config.threshold_min;
                            if (chart.metric === 'pressure' && unitPreferences) {
                                convertedMin = unitConversionUtils.convertPressure(config.threshold_min, 'PSI');
                            } else if (chart.metric === 'temperature' && unitPreferences) {
                                convertedMin = unitConversionUtils.convertTemperature(config.threshold_min, 'C');
                            } else if (chart.metric === 'acceleration' && unitPreferences) {
                                convertedMin = unitConversionUtils.convertAcceleration(config.threshold_min, 'G');
                            }
                            minThreshold = Math.min(minThreshold, parseFloat(convertedMin));
                        }
                    });
                }
            });
        }

        const getDefaultRanges = () => {
            if (!unitPreferences) {
                return {
                    'pressure': { min: 0, max: 150 },
                    'temperature': { min: -20, max: 100 },
                    [t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration')]: { min: 0, max: 5 }
                };
            }

            const ranges = {
                'pressure': { min: 0, max: 150 },
                'temperature': { min: -20, max: 100 },
                [t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration')]: { min: 0, max: 5 }
            };

            if (chart.metric === 'pressure') {
                if (unitPreferences.pressure_unit === 'BAR') {
                    ranges.pressure = { min: 0, max: 10 };
                } else if (unitPreferences.pressure_unit === 'kPa') {
                    ranges.pressure = { min: 0, max: 1000 };
                }
            } else if (chart.metric === 'temperature') {
                if (unitPreferences.temperature_unit === 'F') {
                    ranges.temperature = { min: -4, max: 212 };
                } else if (unitPreferences.temperature_unit === 'K') {
                    ranges.temperature = { min: 253, max: 373 };
                }
            } else if (chart.metric === 'acceleration') {
                if (unitPreferences.acceleration_unit === 'm/s2') {
                    ranges.acceleration = { min: 0, max: 50 };
                } else if (unitPreferences.acceleration_unit === 'ft/s2') {
                    ranges.acceleration = { min: 0, max: 160 };
                }
            }

            return ranges;
        };

        const defaultRanges = getDefaultRanges();
        const defaultRange = defaultRanges[chart.metric] || { min: 0, max: 100 };

        if (maxThreshold === 0) maxThreshold = defaultRange.max;
        if (minThreshold === Number.MAX_VALUE) minThreshold = defaultRange.min;

        return {
            min: Math.min(minThreshold - 10, defaultRange.min),
            max: maxThreshold + 20
        };
    }, [alarmProfiles, unitPreferences, unitConversionUtils]);

    // Create Highcharts options
    const createChartOptions = useCallback((chart) => {
        const chartData = processChartData(chart.metric, chart);
        const profilePlotLines = getAlarmProfilePlotLines(chart);
        const yAxisRange = getYAxisRange(chart);
        const xAxisFormat = getXAxisFormat(selectedTimeRange, {
            formatDateLocal,
            formatTimeLocal,
            formatDayMonthLocal
        });

        // Obtener eventos del sistema procesados
        const { plotBands, plotLines } = processSystemEvents();

        // Obtener separadores de días para vista 1D
        const daySeparators = calculateDaySeparators();

        // Combinar plotLines de eventos del sistema con separadores de días
        const allXAxisPlotLines = [...plotLines, ...daySeparators];

        return {
            chart: {
                type: 'line',
                backgroundColor: 'transparent',
                height: fullscreenChart === chart.id ? 600 : 450,
                animation: { duration: 1000, easing: 'easeOutQuart' },
                zoomType: 'x',
                panning: { enabled: true, type: 'x' },
                panKey: 'shift',
                events: {
                    mouseMove: function (e) {
                        if (e.target && setHoveredTimestamp) {
                            const chart = this;
                            const event = chart.pointer.normalize(e);

                            let closestPoint = null;
                            let minDistance = Infinity;

                            chart.series.forEach(series => {
                                if (series.visible && series.points) {
                                    series.points.forEach(point => {
                                        if (point.plotX !== undefined) {
                                            const distance = Math.abs(point.plotX - event.chartX + chart.plotLeft);
                                            if (distance < minDistance) {
                                                minDistance = distance;
                                                closestPoint = point;
                                            }
                                        }
                                    });
                                }
                            });

                            if (closestPoint && minDistance < 50) {
                                setHoveredTimestamp(closestPoint.x);
                            }
                        }
                    },
                    mouseOut: function () {
                        if (setHoveredTimestamp) {
                            setHoveredTimestamp(null);
                        }
                    }
                }
            },
            title: {
                text: vehicle?.display_name || vehicle?.name || t('hardcoded.components_schematics_vehicleschematiccard.vehicle_id', { id: vehicle?.id }),
                style: {
                    color: theme.palette.text.primary,
                    fontSize: '16px',
                    fontWeight: 'bold'
                }
            },
            xAxis: {
                type: 'datetime',
                labels: {
                    style: { color: theme.palette.text.secondary },
                    ...xAxisFormat
                },
                dateTimeLabelFormats: xAxisFormat.dateTimeLabelFormats,
                gridLineColor: theme.palette.divider,
                crosshair: {
                    width: 2,
                    color: theme.palette.warning.main,
                    dashStyle: 'Solid',
                    zIndex: 10
                },
                // Agregar bandas de eventos del sistema
                plotBands: plotBands,
                // Combinar líneas de eventos puntuales + separadores de días
                plotLines: allXAxisPlotLines
            },
            yAxis: {
                title: {
                    text: `${getMetricDisplayName(chart.metric)} (${getMetricUnit(chart.metric, unitPreferences)})`,
                    style: { color: theme.palette.text.primary, fontWeight: 'bold' }
                },
                labels: { style: { color: theme.palette.text.secondary } },
                gridLineColor: theme.palette.divider,
                plotLines: profilePlotLines,
                min: yAxisRange.min,
                max: yAxisRange.max,
                startOnTick: false,
                endOnTick: false
            },
            tooltip: {
                shared: true,
                crosshairs: true,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                borderColor: theme.palette.divider,
                borderRadius: 8,
                shadow: true,
                useHTML: true,
                zIndex: 999999,
                formatter: function () {
                    let tooltip = `<div style="padding: 8px; z-index: 999999;">`;
                    tooltip += `<div style="font-weight: bold; color: ${theme.palette.primary.main}; margin-bottom: 8px;">`;
                    tooltip += `${formatDateTimeLocal(this.x)}</div>`;

                    this.points.forEach(point => {
                        tooltip += `<div style="margin-bottom: 4px;">`;
                        tooltip += `<span style="color: ${point.color};">●</span> `;
                        tooltip += `<strong>${point.series.name}:</strong> `;
                        tooltip += `${formatValue(point.y, chart.metric, unitPreferences)}`;
                        tooltip += `</div>`;
                    });

                    tooltip += `</div>`;
                    return tooltip;
                },
                positioner: function (labelWidth, labelHeight, point) {
                    const chart = this.chart;
                    const plotLeft = chart.plotLeft;
                    const plotTop = chart.plotTop;
                    const plotWidth = chart.plotWidth;
                    const plotHeight = chart.plotHeight;

                    let tooltipX = point.plotX + plotLeft - labelWidth / 2;
                    let tooltipY = point.plotY + plotTop - labelHeight - 15;

                    if (tooltipX < plotLeft) tooltipX = plotLeft;
                    if (tooltipX + labelWidth > plotLeft + plotWidth) tooltipX = plotLeft + plotWidth - labelWidth;
                    if (tooltipY < plotTop) tooltipY = point.plotY + plotTop + 15;

                    return { x: tooltipX, y: tooltipY };
                }
            },
            legend: {
                enabled: true,
                align: 'center',
                verticalAlign: 'bottom',
                layout: t('hardcoded.components_schematics_vehicletelemetrymodal.horizontal'),
                itemStyle: { color: theme.palette.text.primary, fontWeight: 'normal' },
                itemHoverStyle: { color: theme.palette.text.primary }
            },
            plotOptions: {
                line: {
                    animation: { duration: 1000 },
                    lineWidth: 2,
                    states: { hover: { lineWidth: 3 } },
                    marker: {
                        enabled: true,
                        radius: 3,
                        states: { hover: { radius: 6 } }
                    },
                    point: {
                        events: {
                            mouseOver: function () {
                                if (setHoveredTimestamp) {
                                    setHoveredTimestamp(this.x);
                                }
                            }
                        }
                    }
                },
                series: {
                    stickyTracking: false
                }
            },
            series: chartData.series,
            credits: { enabled: false },
            exporting: {
                enabled: true,
                filename: t('hardcoded.components_schematics_vehicletelemetrymodal.telemetria_display_name_metric_formatdatelocal', { display_name: vehicle?.display_name, metric: chart.metric, formatDateLocal: formatDateLocal(new Date().getTime()) })
            },
            boost: HIGHCHARTS_BOOST_CONFIG
        };
    }, [processChartData, getAlarmProfilePlotLines, getYAxisRange, fullscreenChart, theme, formatValue, getMetricDisplayName, getMetricUnit, formatDateLocal, formatTimeLocal, formatDayMonthLocal, formatDateTimeLocal, selectedTimeRange, unitPreferences, vehicle, setHoveredTimestamp, processSystemEvents, calculateDaySeparators]);

    // Shimmer loading component
    const ShimmerChart = () => (
        <Box sx={{ height: 450, p: 2 }}>
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={350} sx={{ borderRadius: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
                <Skeleton variant="circular" width={12} height={12} />
                <Skeleton variant="circular" width={12} height={12} />
                <Skeleton variant="circular" width={12} height={12} />
            </Box>
        </Box>
    );

    // Handle position click
    const handlePositionClick = useCallback((position) => {
        if (position === 'all') {
            setSelectedPositions(prev => {
                if (prev.includes('all')) {
                    return [];
                }
                return ['all'];
            });
        } else {
            setSelectedPositions(prev => {
                if (prev.includes('all')) {
                    const allPositions = getAvailablePositions(vehicle);
                    const newSelected = allPositions.filter(p => p !== position);
                    return newSelected.length > 0 ? newSelected : [];
                }

                if (prev.includes(position)) {
                    const newSelected = prev.filter(p => p !== position);
                    return newSelected.length > 0 ? newSelected : [];
                }

                const newSelected = [...prev, position];
                const allPositions = getAvailablePositions(vehicle);
                if (newSelected.length === allPositions.length) {
                    return ['all'];
                }

                return newSelected;
            });
        }
    }, [setSelectedPositions, vehicle]);

    // Toggle event type visibility
    const handleEventTypeToggle = useCallback((eventType) => {
        setVisibleEventTypes(prev => ({
            ...prev,
            [eventType]: !prev[eventType]
        }));
    }, []);

    // Chart management
    const addChart = useCallback(() => {
        const newChart = { id: nextChartId, metric: 'temperature', zoomDomain: null };
        setActiveCharts(prev => [...prev, newChart]);
        setNextChartId(prev => prev + 1);
    }, [nextChartId]);

    const removeChart = useCallback((chartId) => {
        if (activeCharts.length > 1) {
            setActiveCharts(prev => prev.filter(chart => chart.id !== chartId));
        }
    }, [activeCharts.length]);

    const updateChartMetric = useCallback((chartId, metric) => {
        setActiveCharts(prev =>
            prev.map(chart =>
                chart.id === chartId ? { ...chart, metric, zoomDomain: null } : chart
            )
        );
        setProcessedDataCache({});
    }, []);

    // Handle time range selection
    const handleTimeRangeSelection = useCallback((rangeType) => {
        if (isAutoSwitchingRef.current) return;

        setIsLoadingRange(true);
        isAutoSwitchingRef.current = true;

        let newStartDate, newEndDate;

        if (rangeType === '1hour') {
            newStartDate = new Date().toISOString().split('T')[0];
            newEndDate = new Date().toISOString().split('T')[0];
        } else {
            const dates = calculateDateRange(rangeType);
            newStartDate = dates.startDate;
            newEndDate = dates.endDate;
        }

        setSelectedTimeRange(rangeType);
        setCustomDateRange({
            enabled: true,
            startDate: newStartDate,
            endDate: newEndDate
        });

        setTimeout(() => {
            setIsLoadingRange(false);
            isAutoSwitchingRef.current = false;
        }, 300);
    }, [calculateDateRange, setSelectedTimeRange, setCustomDateRange]);

    // Handle custom date change
    const handleCustomDateChange = useCallback((field, value) => {
        setCustomDateRange(prev => ({
            ...prev,
            [field]: value,
            enabled: true
        }));
    }, [setCustomDateRange]);

    // Render chart
    const renderChart = useCallback((chart) => {
        if (isLoadingRange || telemetryLoading) {
            return <ShimmerChart />;
        }

        const chartData = processChartData(chart.metric, chart);

        if (selectedPositions.length === 0) {
            return (
                <Box sx={{
                    height: 450,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    border: 2,
                    borderColor: 'divider',
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    bgcolor: 'background.default'
                }}>
                    <Typography variant="h6" color="text.secondary">{t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.no_hay_posiciones_seleccionadas')}</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.selecciona_al_menos_una_posicion')}</Typography>
                </Box>
            );
        }

        if (chartData.series.length === 0) {
            return (
                <Box sx={{
                    height: 450,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    border: 2,
                    borderColor: 'divider',
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    bgcolor: 'background.default'
                }}>
                    <Typography variant="h6" color="text.secondary">{t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.no_hay_datos_disponibles')}</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.no_se_encontraron_datos_de')}{chart.metric}.<br />
                        {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.intenta_seleccionar_diferentes_posiciones_o')}</Typography>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refetchTelemetry} size="small">
                        {i18n.t('hardcoded.components_schematics_vehicleroutemapmodal.actualizar_datos')}</Button>
                </Box>
            );
        }

        const options = createChartOptions(chart);

        return (
            <Box sx={{ position: 'relative' }}>
                {/* Decirlo es parte del dato: una gráfica a resolución reducida
                    sigue siendo fiel en forma, pero el usuario tiene que saber
                    que no está viendo lectura por lectura. */}
                {telemetryMeta?.reducida && telemetryMeta?.disponibles > 0 && (
                    <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 0.5, textAlign: 'right' }}>
                        {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.resolucion_reducida', {
                            devueltas: telemetryMeta.devueltas?.toLocaleString('es-MX'),
                            disponibles: telemetryMeta.disponibles?.toLocaleString('es-MX'),
                        })}
                    </Typography>
                )}
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                    ref={(ref) => {
                        if (ref) chartRefs.current[chart.id] = ref;
                    }}
                />
                <IconButton
                    size="small"
                    onClick={() => setFullscreenChart(fullscreenChart === chart.id ? null : chart.id)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'primary.main', color: 'white' }
                    }}
                >
                    <FullscreenIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    }, [processChartData, createChartOptions, fullscreenChart, refetchTelemetry, isLoadingRange, telemetryLoading, selectedPositions, telemetryMeta, t]);

    // Vehicle schematic filter
    const VehicleSchematicFilter = useMemo(() => {
        const layout = createSchematicLayout(vehicle);
        const availablePositions = getAvailablePositions(vehicle);

        if (layout.length === 0) {
            return (
                <Box sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    borderRadius: 1.5,
                    p: leftPanelCollapsed ? 1 : 1.5,
                    bgcolor: 'background.paper',
                    mb: leftPanelCollapsed ? 1 : 2
                }}>
                    {!leftPanelCollapsed && (
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom textAlign="center">
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.filtro_de_posiciones')}</Typography>
                    )}
                    {!leftPanelCollapsed && (
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.no_hay_configuracion_de_esquematico')}</Typography>
                    )}
                </Box>
            );
        }

        return (
            <Box sx={{
                border: 1,
                borderColor: 'primary.main',
                borderRadius: 1.5,
                p: leftPanelCollapsed ? 1 : 1.5,
                bgcolor: 'background.paper',
                mb: leftPanelCollapsed ? 1 : 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Box
                        onClick={() => handlePositionClick('all')}
                        sx={{
                            width: leftPanelCollapsed ? 30 : 40,
                            height: leftPanelCollapsed ? 20 : 25,
                            border: '2px solid',
                            borderColor: selectedPositions.includes('all') ? 'success.main' : 'grey.400',
                            backgroundColor: selectedPositions.includes('all') ? 'success.main' : 'grey.400',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { transform: 'scale(1.05)', boxShadow: theme.shadows[2] }
                        }}
                    >
                        <Typography variant="body2" sx={{
                            color: 'white',
                            fontWeight: 700,
                            fontSize: leftPanelCollapsed ? '0.6rem' : '0.65rem'
                        }}>
                            ALL
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, minHeight: leftPanelCollapsed ? 40 : 60 }}>
                    {layout.map((row, rowIndex) => {
                        const getRowJustification = () => {
                            if (row.length === 1) return 'center';
                            if (row.length === 2) return Math.abs(row[1] - row[0]) === 1 ? 'space-between' : 'center';
                            if (row.length <= 4) return 'space-evenly';
                            return 'center';
                        };

                        return (
                            <Box
                                key={`schematic-row-${rowIndex}`}
                                sx={{
                                    display: 'flex',
                                    gap: 0.6,
                                    justifyContent: getRowJustification(),
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    maxWidth: '100%',
                                    width: 'fit-content'
                                }}
                            >
                                {row.map(position => {
                                    if (!availablePositions.includes(position)) return null;

                                    const isSelected = selectedPositions.includes('all') || selectedPositions.includes(position);

                                    return (
                                        <Box
                                            key={t('hardcoded.components_schematics_vehicletelemetrymodal.schematic-position-position', { position: position })}
                                            onClick={() => handlePositionClick(position)}
                                            sx={{
                                                width: leftPanelCollapsed ? 28 : 35,
                                                height: leftPanelCollapsed ? 28 : 35,
                                                border: '2px solid',
                                                borderColor: isSelected ? 'primary.main' : 'grey.400',
                                                backgroundColor: isSelected ? 'primary.main' : 'grey.400',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': { transform: 'scale(1.1)', boxShadow: theme.shadows[2] }
                                            }}
                                        >
                                            <Typography variant="body2" sx={{
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: leftPanelCollapsed ? '0.65rem' : '0.7rem'
                                            }}>
                                                {position}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }, [vehicle, selectedPositions, handlePositionClick, theme, leftPanelCollapsed]);

    // System Events Controls Component
    const SystemEventsControls = useMemo(() => {
        if (leftPanelCollapsed) return null;

        const eventTypeConfig = [
            {
                key: 'HB ONLINE',
                label: 'Online',
                icon: <OnlineIcon sx={{ fontSize: 16 }} />,
                color: '#4CAF50'
            },
            {
                key: 'HB OFFLINE',
                label: 'Offline',
                icon: <OfflineIcon sx={{ fontSize: 16 }} />,
                color: '#9E9E9E'
            },
            {
                key: 'POWEROFF',
                label: 'Power Off',
                icon: <PowerOffIcon sx={{ fontSize: 16 }} />,
                color: '#F44336'
            },
            {
                key: 'STARTUP',
                label: t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.startup'),
                icon: <PowerIcon sx={{ fontSize: 16 }} />,
                color: '#4CAF50'
            }
        ];

        return (
            <Paper sx={{ p: 1.5, bgcolor: 'background.paper', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '0.85rem', mb: 1 }}>
                    {t('hardcoded.components_schematics_receivereventsmodal.eventos_del_receptor')}</Typography>
                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {eventTypeConfig.map(({ key, label, icon, color }) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Switch
                                    checked={visibleEventTypes[key]}
                                    onChange={() => handleEventTypeToggle(key)}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: color,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: color,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ color: color, display: 'flex', alignItems: 'center' }}>
                                        {icon}
                                    </Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        {label}
                                    </Typography>
                                </Box>
                            }
                            sx={{ m: 0 }}
                        />
                    ))}
                </Box>

                {systemEventsLoading && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.cargando_eventos')}</Typography>
                    </Box>
                )}
            </Paper>
        );
    }, [leftPanelCollapsed, visibleEventTypes, handleEventTypeToggle, systemEventsLoading]);

    return (
        <Box sx={{
            display: 'flex',
            height: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            overflow: 'hidden'
        }}>
            {/* Panel izquierdo */}
            {!isMobile && (
                <Box sx={{
                    width: leftPanelCollapsed ? '60px' : '240px',
                    minWidth: leftPanelCollapsed ? '60px' : '240px',
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    position: 'relative',
                    transition: 'all 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Box sx={{
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: leftPanelCollapsed ? 'center' : 'space-between',
                        bgcolor: 'background.paper',
                        minHeight: 56
                    }}>
                        <IconButton
                            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                            size="small"
                            sx={{
                                bgcolor: leftPanelCollapsed ? 'primary.main' : 'transparent',
                                color: leftPanelCollapsed ? 'white' : 'text.primary',
                                '&:hover': {
                                    bgcolor: leftPanelCollapsed ? 'primary.dark' : 'action.hover'
                                }
                            }}
                        >
                            {leftPanelCollapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
                        </IconButton>
                    </Box>

                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: leftPanelCollapsed ? 1 : 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: leftPanelCollapsed ? 1 : 1.5
                    }}>
                        {VehicleSchematicFilter}

                        {!leftPanelCollapsed && (
                            <Paper sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                    {TIME_RANGE_OPTIONS.map(option => (
                                        <Chip
                                            key={option.value}
                                            label={option.label}
                                            variant={selectedTimeRange === option.value ? 'filled' : 'outlined'}
                                            color={selectedTimeRange === option.value ? 'primary' : 'default'}
                                            onClick={() => handleTimeRangeSelection(option.value)}
                                            size="small"
                                            sx={{ cursor: 'pointer', fontSize: '0.7rem', transition: 'all 0.2s' }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                        type="date"
                                        value={customDateRange.startDate}
                                        onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                                        size="small"
                                        label={t('hardcoded.components_schematics_chartsview.fecha_inicio')}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={selectedTimeRange === '1hour'}
                                    />
                                    <TextField
                                        type="date"
                                        value={customDateRange.endDate}
                                        onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                                        size="small"
                                        label={t("hardcoded.components_schematics_vehicletelemetrymodal_chartstab.fecha_fin")}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={selectedTimeRange === '1hour'}
                                    />
                                </Box>
                            </Paper>
                        )}

                        {SystemEventsControls}

                        {!leftPanelCollapsed ? (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={addChart}
                                fullWidth
                                size="small"
                                sx={{
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                                    '&:hover': {
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                {t('hardcoded.components_schematics_chartsview.agregar_grafica')}</Button>
                        ) : (
                            <IconButton
                                onClick={addChart}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    alignSelf: 'center',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                                title={t('hardcoded.components_schematics_chartsview.agregar_grafica')}
                            >
                                <AddIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            )}

            {/* Panel móvil */}
            {isMobile && (
                <Box sx={{
                    width: '100%',
                    maxHeight: leftPanelCollapsed ? '80px' : '500px',
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    transition: 'all 0.3s ease-in-out',
                    overflowY: leftPanelCollapsed ? 'hidden' : 'auto'
                }}>
                    <Box sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderBottom: leftPanelCollapsed ? 0 : 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'primary.50',
                        minHeight: { xs: 60, sm: 56 },
                        boxShadow: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MenuIcon color="primary" sx={{ fontSize: 20 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'primary.main' }}>
                                {leftPanelCollapsed ? t('hardcoded.components_schematics_chartsview.mostrar_controles') : t('hardcoded.components_schematics_chartsview.controles_de_filtros')}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                            size="medium"
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                                boxShadow: 2
                            }}
                        >
                            {leftPanelCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                        </IconButton>
                    </Box>

                    {!leftPanelCollapsed && (
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {VehicleSchematicFilter}

                            <Paper sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '0.9rem' }}>
                                    {t('hardcoded.components_schematics_chartsview.rango_de_tiempo')}</Typography>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                                    {TIME_RANGE_OPTIONS.map(option => (
                                        <Chip
                                            key={option.value}
                                            label={option.label}
                                            variant={selectedTimeRange === option.value ? 'filled' : 'outlined'}
                                            color={selectedTimeRange === option.value ? 'primary' : 'default'}
                                            onClick={() => handleTimeRangeSelection(option.value)}
                                            size="small"
                                            sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <IconButton
                                        onClick={addChart}
                                        size="small"
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'primary.dark' },
                                            boxShadow: 1
                                        }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('hardcoded.components_schematics_chartsview.agregar_grafica')}</Typography>
                                </Box>
                            </Paper>

                            <Paper sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '0.85rem', mb: 1 }}>
                                    {t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.eventos_del_sistema')}</Typography>
                                <Divider sx={{ mb: 1.5 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {[
                                        { key: 'HB ONLINE', label: 'Online', icon: <OnlineIcon />, color: '#4CAF50' },
                                        { key: 'HB OFFLINE', label: 'Offline', icon: <OfflineIcon />, color: '#9E9E9E' },
                                        { key: 'POWEROFF', label: 'Power Off', icon: <PowerOffIcon />, color: '#F44336' },
                                        { key: 'STARTUP', label: t('hardcoded.components_schematics_vehicletelemetrymodal_chartstab.startup'), icon: <PowerIcon />, color: '#4CAF50' }
                                    ].map(({ key, label, icon, color }) => (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Switch
                                                    checked={visibleEventTypes[key]}
                                                    onChange={() => handleEventTypeToggle(key)}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: color,
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: color,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ color: color, display: 'flex', alignItems: 'center' }}>
                                                        {icon}
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ m: 0 }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            {/* Panel central de gráficos */}
            <Box sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: { xs: 1, sm: 2 }
                }}>
                    {activeCharts.map((chart) => (
                        <Paper
                            key={chart.id}
                            sx={{
                                p: { xs: 1, sm: 2 },
                                mb: 2,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    boxShadow: theme.shadows[8],
                                    transform: 'translateY(-1px)'
                                },
                                '&:last-child': {
                                    mb: 0
                                }
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                flexWrap: 'wrap',
                                gap: 1
                            }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <ToggleButtonGroup
                                        value={chart.metric}
                                        exclusive
                                        onChange={(e, value) => value && updateChartMetric(chart.id, value)}
                                        size="small"
                                    >
                                        <ToggleButton value="pressure">
                                            <PressureIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: 16 }} />
                                            {!isSmallMobile && ''}
                                        </ToggleButton>
                                        <ToggleButton value="temperature">
                                            <TemperatureIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: 16 }} />
                                            {!isSmallMobile && ''}
                                        </ToggleButton>
                                        <ToggleButton value="acceleration">
                                            <AccelerationIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: 16 }} />
                                            {!isSmallMobile && ''}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                {activeCharts.length > 1 && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeChart(chart.id)}
                                        title={t('hardcoded.components_schematics_chartsview.eliminar_grafica')}
                                        sx={{ transition: 'all 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                )}
                            </Box>

                            {renderChart(chart)}
                        </Paper>
                    ))}

                    {activeCharts.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="h6" color="text.secondary">
                                {t('hardcoded.components_schematics_chartsview.no_hay_graficas_activas')}</Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {t('hardcoded.components_schematics_chartsview.utiliza_el_boton_agregar_grafica')}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ChartsTab;